import {
  ApolloClient,
  ApolloError,
  DocumentNode,
  NormalizedCacheObject,
  OperationVariables,
  TypedDocumentNode,
  FetchPolicy,
  WatchQueryFetchPolicy,
} from '@apollo/client/core';

import { Mutation, Query } from '@/generated/graphql';
import ErrorHandler from '@/helpers/errorHandler.ts';
import { apolloClient } from '@/apollo';
import { FileActions } from '@/helpers/types';
import notificationHandler from '@/helpers/notificationHandler.ts';
import { clearToken } from '@/helpers/authUtils.ts';

class ApolloRequestsService {
  private static instance: ApolloRequestsService;
  private apolloClient: ApolloClient<NormalizedCacheObject>;

  private constructor(client: ApolloClient<NormalizedCacheObject>) {
    this.apolloClient = client;
  }

  public static getInstance(client: ApolloClient<NormalizedCacheObject>): ApolloRequestsService {
    if (!ApolloRequestsService.instance) {
      ApolloRequestsService.instance = new ApolloRequestsService(client);
    }

    return ApolloRequestsService.instance;
  }

  public async makeApolloRequest(
    type: 'mutation' | 'query',
    request: DocumentNode | TypedDocumentNode<Query, OperationVariables>,
    variables?: object,
    action?: FileActions,
    showNotification: boolean = true,
    options?: {
      fetchPolicy?: FetchPolicy | WatchQueryFetchPolicy;
      customErrorMessage?: string;
    },
  ): Promise<Query | Mutation | null | undefined> {
    try {
      // TODO: refactor this to one call without ifs???
      let result;
      if (type === 'mutation') {
        const { data } = await this.apolloClient.mutate<Mutation>({
          mutation: request,
          variables,
        });
        result = data;
      } else {
        const fetchPolicy = options?.fetchPolicy ?? 'no-cache';

        if (fetchPolicy === 'cache-and-network') {
          const { data } = await this.apolloClient.watchQuery<Query>({
            query: request,
            variables,
            fetchPolicy: fetchPolicy as WatchQueryFetchPolicy,
          }).result();
          result = data;
        } else {
          const { data } = await this.apolloClient.query<Query>({
            query: request,
            variables,
            fetchPolicy: fetchPolicy as FetchPolicy,
          });
          result = data;
        }
      }
      if (action) notificationHandler(action);
      return result;
    } catch (e) {
      new ErrorHandler(e as Error, showNotification, options?.customErrorMessage).handleError();
      const error = e as ApolloError;
      console.error('Error:', error);
      if (error.message === 'Authorization required' || error.message === 'Access denied') {
        clearToken('sid');
        window.location.replace(`${window.location.origin}/login`);
      }
      throw new Error(error.message);
    }
  }
}

export const apolloService = ApolloRequestsService.getInstance(apolloClient);
